import { useState, useMemo, useEffect } from 'react';
import { Calendar, luxonLocalizer, Views, Components } from 'react-big-calendar';
import { DateTime, Settings } from 'luxon';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useScheduledActivities } from '../hooks/useScheduledActivities';
import { getYearMonth, isTodayOrFuture } from '../utils/dateHelpers';
import { transformToCalendarEvents, CalendarEvent } from '../utils/calendarHelpers';
import { motion } from 'framer-motion';
import { ManageScheduledActivitiesModal } from '../components/calendar/ManageScheduledActivitiesModal';
import { CustomEventComponent } from '../components/calendar/CustomEventComponent';
import { CustomDateCellHeader } from '../components/calendar/CustomDateCellHeader';

// Configure Luxon for UK timezone
Settings.defaultZone = 'Europe/London';
const localizer = luxonLocalizer(DateTime, { firstDayOfWeek: 1 }); // Monday start

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();

  // Update document title
  useEffect(() => {
    document.title = 'Calendar | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  // Get year and month from current date for query
  const { year, month } = useMemo(() => getYearMonth(currentDate), [currentDate]);

  // Fetch scheduled activities for current month
  const { data, isLoading, error } = useScheduledActivities({ year, month });

  // Transform scheduled activities to calendar events
  const events = useMemo(() => {
    if (!data?.scheduledActivities) return [];
    return transformToCalendarEvents(data.scheduledActivities);
  }, [data]);

  // Handle navigation (month change)
  const handleNavigate = (newDate: Date) => {
    setCurrentDate(newDate);
  };

  const handleSelectSlot = (slotInfo: { start: Date; end: Date; action: string }) => {
    if (isTodayOrFuture(slotInfo.start)) {
      setSelectedDate(slotInfo.start);
      setManageModalOpen(true);
    }
  };

  const handleSelectEvent = (event: CalendarEvent) => {
    if (isTodayOrFuture(event.start)) {
      setSelectedDate(event.start);
      setManageModalOpen(true);
    }
  };

  const handleShowMore = (_events: CalendarEvent[], date: Date) => {
    if (isTodayOrFuture(date)) {
      setSelectedDate(date);
      setManageModalOpen(true);
    }
  };

  const handleAddClick = (date: Date) => {
    setSelectedDate(date);
    setManageModalOpen(true);
  };

  const handleCloseManageModal = () => {
    setManageModalOpen(false);
    setSelectedDate(undefined);
  };

  // Custom components for calendar
  const components: Components<CalendarEvent> = {
    event: (props) => (
      <CustomEventComponent {...props} />
    ),
    month: {
      dateHeader: ({ date, label }) => (
        <CustomDateCellHeader date={date} label={label} onAddClick={handleAddClick} />
      ),
    },
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-red-600">
          <p className="font-medium">Failed to load calendar</p>
          <p className="mt-1 text-sm">{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Activity Calendar</h1>
          <p className="mt-2 text-sm text-gray-600">
            Schedule activities to appear on specific days
          </p>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600">Loading calendar...</p>
            </div>
          </div>
        ) : (
          <div style={{ height: '700px' }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              views={[Views.MONTH]}
              defaultView={Views.MONTH}
              date={currentDate}
              onNavigate={handleNavigate}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              onShowMore={handleShowMore}
              selectable
              components={components}
              toolbar={true}
              popup={false}
              style={{ height: '100%' }}
            />
          </div>
        )}
      </div>

      {/* Info panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Tip:</strong> Click on a date to manage scheduled activities.
          Drag activities between time periods to reorganize them.
        </p>
      </div>

      {/* Modal */}
      {selectedDate && (
        <ManageScheduledActivitiesModal
          open={manageModalOpen}
          onClose={handleCloseManageModal}
          date={selectedDate}
        />
      )}
    </motion.div>
  );
}
